import { useEffect, useMemo, useState } from 'react';
import { loadFieldReportQtyHistory } from '../../db/company';

// Shared prior-installs fetch (api/readFieldReportQtyHistory) for the Work-tab
// Gantt annotations and the phase time card. One in-flight/settled promise per
// (buId, reportId, reportDate) so the components on the same step don't each
// hit the API.
const cache = new Map();

const fetchHistory = (buId, reportId, reportDate) => {
    const key = `${buId}|${reportId}|${reportDate}`;
    if (!cache.has(key)) {
        cache.set(key, loadFieldReportQtyHistory({ buId, reportId, reportDate })
            .then((res) => (res && !res.error ? res : null))
            .catch(() => null));
        // Keep the cache small — one report open at a time.
        if (cache.size > 8) cache.delete(cache.keys().next().value);
    }
    return cache.get(key);
};

const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
};

// { budgets: {code: qty}, schedule: {code: {qty, uom, description, hours, start, finish}},
//   toDate: {code: qty}, last7: {code: qty}, daily: {code: [{date, qty}]},
//   dailyBySize: {code: {size: {date: qty}}}, sizeToDate: {code: {size: qty}},
//   sizes: [label],
//   planned: {code: {perDay, start, end, total}}, planFor(code), loaded }
const useQtyHistory = (report) => {
    const [res, setRes] = useState(null);
    const buId = report?.buId;
    const reportId = report?.reportId;
    const reportDate = report?.reportDate;

    useEffect(() => {
        let alive = true;
        if (!buId || !reportId || !reportDate) return undefined;
        fetchHistory(buId, reportId, reportDate).then((r) => { if (alive) setRes(r); });
        return () => { alive = false; };
    }, [buId, reportId, reportDate]);

    return useMemo(() => {
        const budgets = {};
        const schedule = {};
        const toDate = {};
        const last7 = {};
        for (const b of res?.budgets || []) {
            const q = num(b.BudgetQty);
            if (q) budgets[String(b.CostCode || '').trim()] = q;
        }
        // Schedule & Baseline page totals per cost code. They win over budgets
        // carried forward from prior reports — the schedule page is the
        // authority; prior-report budgets stay as the fallback. (Absent until
        // the function deploy carries the fifth recordset.)
        for (const s of res?.schedule || []) {
            const code = String(s.Cost_Code || '').trim();
            const q = num(s.Budget_Qty);
            if (!code || !q) continue;
            schedule[code] = {
                qty: q,
                uom: String(s.UOM || '').trim(),
                description: String(s.Description || '').trim(),
                hours: num(s.Budget_Hours),
                // Planned window per code (absent until the function deploy
                // carries the extra columns).
                start: s.Start_Date ? String(s.Start_Date).slice(0, 10) : null,
                finish: s.Finish_Date ? String(s.Finish_Date).slice(0, 10) : null,
            };
            budgets[code] = q;
        }
        // Per cost code, and per code+size (Task) for the phase card's sub-rows.
        const sizeToDate = {};
        for (const h of res?.history || []) {
            const code = String(h.CostCode || '').trim();
            const size = String(h.Task || '').trim();
            toDate[code] = (toDate[code] || 0) + num(h.ToDate);
            last7[code] = (last7[code] || 0) + num(h.Last7);
            if (size) {
                const bySize = (sizeToDate[code] = sizeToDate[code] || {});
                bySize[size] = (bySize[size] || 0) + num(h.ToDate);
            }
        }
        // Per-day submissions (absent until the function deploy carries the
        // fourth recordset — everything renders fine without it). Task splits
        // the same rows by pipe size; rows predating the Task column land under
        // '' and only feed the code-level total.
        const daily = {};
        const dailyBySize = {};
        for (const d of res?.daily || []) {
            const code = String(d.CostCode || '').trim();
            const date = String(d.ReportDate || '').slice(0, 10);
            const size = String(d.Task || '').trim();
            if (!code || !date) continue;
            (daily[code] = daily[code] || []).push({ date, qty: num(d.Qty) });
            if (size) {
                const bySize = (dailyBySize[code] = dailyBySize[code] || {});
                const byDay = (bySize[size] = bySize[size] || {});
                byDay[date] = (byDay[date] || 0) + num(d.Qty);
            }
        }
        Object.values(daily).forEach((list) => list.sort((a, b) => a.date.localeCompare(b.date)));
        // The active quantity schedule's per-day planned curve (absent until
        // the function deploy carries the sixth recordset — everything falls
        // back to the Burn-Tool even spread without it).
        const planned = {};
        for (const p of res?.planned || []) {
            const code = String(p.Cost_Code || '').trim();
            const date = String(p.Period_Date || '').slice(0, 10);
            const q = num(p.Period_Qty);
            if (!code || !date || !q) continue;
            const entry = (planned[code] = planned[code] || { perDay: {}, start: null, end: null, total: 0 });
            entry.perDay[date] = (entry.perDay[date] || 0) + q;
            entry.total += q;
            if (!entry.start || date < entry.start) entry.start = date;
            if (!entry.end || date > entry.end) entry.end = date;
        }
        // One decision point for the wizard: a non-null plan means "pace this
        // phase against the saved schedule", null means Burn-Tool behavior.
        const planFor = (code) => planned[String(code || '').trim()] || null;
        return {
            budgets, schedule, toDate, last7, daily, dailyBySize, sizeToDate,
            sizes: Array.isArray(res?.sizes) ? res.sizes : null,
            planned, planFor, loaded: !!res,
        };
    }, [res]);
};

export default useQtyHistory;
